import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import {
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineSeparator,
} from '@mui/lab';
import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { useEditUser } from '../../sic-lib/api/hooks/useUser';
import { EditUserData } from '../../sic-lib/types/NewEditUser';
import CreateEditUser from '../Settings/Users/CreateEdit/CreateEditUser';
import AxiosErrorAlert from '../Shared/Alerts/AxiosErrorAlert';

interface Props {
    userName: string;
    firstName: string | undefined;
    lastName: string | undefined;
    userPhone: string | undefined;
    userEmail: string | undefined;
}

export function UserDetailsTimelineItem({
    userName,
    firstName,
    lastName,
    userPhone,
    userEmail,
}: Props) {
    const [showSetupUserDetails, setShowSetupUserDetails] = useState(false);

    const handleShowChangeEmail = () => {
        setShowSetupUserDetails(true);
    };

    const { data: user, isLoading, isError, error } = useEditUser(userName);

    const isSetupComplete = !!firstName && !!lastName && !!userPhone && !!userEmail;

    return (
        <TimelineItem>
            <TimelineSeparator>
                <TimelineDot color={isSetupComplete ? 'success' : undefined}>
                    {!isSetupComplete ? <PersonIcon /> : <CheckIcon />}
                </TimelineDot>
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ display: 'flex', flexDirection: 'column' }}>
                <Grid container flexDirection="column" spacing={2}>
                    <Grid item>
                        <Typography variant="h5">User Details</Typography>
                    </Grid>
                    <Grid item>
                        <Typography color={'text.secondary'}>
                            Setting up an email address in StayInContact enhances your connectivity,
                            enabling you to send and receive emails directly within the interface.
                            This integration streamlines your communication, consolidating multiple
                            channels into one easy-to-manage platform.
                        </Typography>
                    </Grid>
                    {!showSetupUserDetails && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleShowChangeEmail}
                                disabled={isSetupComplete}
                            >
                                Setup My User Details
                            </Button>
                        </Grid>
                    )}
                    {showSetupUserDetails && isLoading && (
                        <Grid item>
                            <CircularProgress />
                        </Grid>
                    )}
                    {showSetupUserDetails && !isLoading && user && (
                        <Grid item>
                            <CreateEditUser<EditUserData> user={user} />
                        </Grid>
                    )}
                    {isError && (
                        <Grid item>
                            <AxiosErrorAlert error={error} />
                        </Grid>
                    )}
                </Grid>
            </TimelineContent>
        </TimelineItem>
    );
}
